import React, { Component } from 'react';
import Table, { ColumnProps } from 'antd/lib/table';
import umbrella from 'umbrella-storage';
import { message } from 'antd';
import ProjectService, { IProjectInfo, IProjectInfoList } from '../../service/project/ProjectService';
import { getProjectStatusText } from '../../components/public/ProjectStatusSelect';
import { timestamp2localdate } from '../../utils/dateTools';
import './page-project.less';

interface ProjectTableProps {
    isMyProjectPage: boolean;
}

interface ProjectTableState {
    pageData: IProjectInfoList | null;
    sourceData: IProjectInfo[];
    beginDate: string | null;
    endDate: string | null;
    statusList: number[] | null;
    serviceCategory: string | null;
    companyId: number | null;
    customerManagerId: number | null;
    projectManagerAdminId: number | null;
    pagination: any;
}

const defaultPageSize = 5;
const getPageSize = () => umbrella.getLocalStorage('projectPageSize') || defaultPageSize;

class ProjectTable extends Component<ProjectTableProps, ProjectTableState> {
    state: ProjectTableState = {
        pageData: { records: [], total: 0, size: 0, current: 1, pages: 0 },
        sourceData: [],
        beginDate: null,
        endDate: null,
        statusList: null,
        serviceCategory: null,
        companyId: null,
        customerManagerId: null,
        projectManagerAdminId: null,
        pagination: {
            current: 1,
            pageSize: getPageSize(),
            total: 0,
        },
    };

    getOperationColumnWidth = (buttonCount: number) => Math.max(96, buttonCount * 68 + 16);

    getOperationButtonCount = () => (this.props.isMyProjectPage ? 0 : 1);

    getColumns = (): ColumnProps<any>[] => {
        const operationWidth = this.getOperationColumnWidth(this.getOperationButtonCount());
        const columns: ColumnProps<any>[] = [
            {
                title: '项目名称',
                width: 120,
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: IProjectInfo) => (
                    <a href={`/#/app/project/view?id=${record.id}`}>{text}</a>
                ),
            },
            { title: '客户名称', width: 100, dataIndex: 'companyName', key: 'companyName' },
            { title: '客户经理', width: 90, dataIndex: 'customerManagerName', key: 'customerManagerName' },
            { title: '项目经理', width: 90, dataIndex: 'projectManagerName', key: 'projectManagerName' },
            { title: '服务类别', width: 110, dataIndex: 'serviceCategory', key: 'serviceCategory' },
            { title: '项目状态', width: 80, dataIndex: 'statusText', key: 'statusText' },
            { title: '开始日期', width: 90, dataIndex: 'startDateText', key: 'startDateText' },
            { title: '结束日期', width: 90, dataIndex: 'endDateText', key: 'endDateText' },
        ];

        if (!this.props.isMyProjectPage) {
            columns.push({
                title: '操作',
                key: 'operation',
                fixed: 'right',
                width: operationWidth,
                render: (record: IProjectInfo) => (
                    <div className="project-operation-buttons">
                        {!this.props.isMyProjectPage && <button className="table-action-btn" onClick={() => this.handleEditButtonClick(record)}>编辑</button>}
                    </div>
                ),
            });
        }
        return columns;
    };

    setPaginationPage = (pageNo: number) => {
        this.setState({ pagination: { ...this.state.pagination, current: pageNo } });
    };

    getProjectTableData = async (
        pageNo: number,
        beginDate?: string | null,
        endDate?: string | null,
        statusList?: number[] | null,
        serviceCategory?: string | null,
        companyId?: number | null,
        customerManagerId?: number | null,
        projectManagerAdminId?: number | null,
    ) => {
        const nextBeginDate = beginDate !== undefined ? beginDate : this.state.beginDate;
        const nextEndDate = endDate !== undefined ? endDate : this.state.endDate;
        const nextStatusList = statusList !== undefined ? statusList : this.state.statusList;
        const nextServiceCategory = serviceCategory !== undefined ? serviceCategory : this.state.serviceCategory;
        const nextCompanyId = companyId !== undefined ? companyId : this.state.companyId;
        const nextCustomerManagerId = customerManagerId !== undefined ? customerManagerId : this.state.customerManagerId;
        const nextProjectManagerAdminId = projectManagerAdminId !== undefined ? projectManagerAdminId : this.state.projectManagerAdminId;

        const request = {
            queryParam: {
                beginDate: nextBeginDate ?? undefined,
                endDate: nextEndDate ?? undefined,
                statusList: nextStatusList ?? undefined,
                serviceCategory: nextServiceCategory ?? undefined,
                companyId: nextCompanyId ?? undefined,
                customerManagerId: nextCustomerManagerId ?? undefined,
                projectManagerAdminId: nextProjectManagerAdminId ?? undefined,
            },
            pageNo,
            pageSize: this.state.pagination.pageSize,
        };

        const response = this.props.isMyProjectPage
            ? await ProjectService.getMyProjectList(request)
            : await ProjectService.getProjectList(request);

        if (response && response.data && response.data.projectInfoList) {
            const pageData: IProjectInfoList = response.data.projectInfoList;
            const records = pageData.records || [];
            const sourceData = records.map((item: any) => ({
                ...item,
                statusText: getProjectStatusText(item.status),
                startDateText: timestamp2localdate(item.startDate, false),
                endDateText: timestamp2localdate(item.endDate, false),
            }));

            this.setState({
                pageData: { ...pageData, current: pageNo },
                sourceData,
                pagination: { ...this.state.pagination, total: pageData.total, current: pageNo },
                beginDate: nextBeginDate,
                endDate: nextEndDate,
                statusList: nextStatusList,
                serviceCategory: nextServiceCategory,
                companyId: nextCompanyId,
                customerManagerId: nextCustomerManagerId,
                projectManagerAdminId: nextProjectManagerAdminId,
            });
        }
    };

    handleChangePage = (pageNo: number) => {
        this.getProjectTableData(pageNo);
    };

    changePageSize = (current: number, pageSize: number) => {
        umbrella.setLocalStorage('projectPageSize', pageSize);
        this.setState({ pagination: { ...this.state.pagination, current: 1, pageSize } }, () => {
            this.getProjectTableData(1);
        });
    };

    handleEditButtonClick = (record: IProjectInfo) => {
        window.location.href = `/#/app/project/manager?id=${record.id}`;
    };

    exportProjectData = async () => {
        if (!this.state.sourceData || this.state.sourceData.length === 0) {
            message.warning('暂无可导出的查询结果，请先查询出结果再导出');
            return;
        }

        const request = {
            queryParam: {
                beginDate: this.state.beginDate ?? undefined,
                endDate: this.state.endDate ?? undefined,
                statusList: this.state.statusList ?? undefined,
                serviceCategory: this.state.serviceCategory ?? undefined,
                companyId: this.state.companyId ?? undefined,
                customerManagerId: this.state.customerManagerId ?? undefined,
                projectManagerAdminId: this.state.projectManagerAdminId ?? undefined,
            },
            pageNo: 1,
            pageSize: this.state.pagination.pageSize,
        };
        const response = await ProjectService.exportProject(request);
        if (!response || !response.data) {
            message.error('导出失败');
            return;
        }

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `project_export_${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        message.success('导出成功');
    };

    render() {
        const dataWithKeys = (this.state.sourceData || []).map((row) => ({ ...row, key: row.id }));

        return (
            <Table
                size="small"
                columns={this.getColumns()}
                dataSource={dataWithKeys}
                tableLayout="fixed"
                pagination={{
                    showSizeChanger: true,
                    current: this.state.pagination.current,
                    pageSize: this.state.pagination.pageSize,
                    total: this.state.pagination.total,
                    pageSizeOptions: ['5', '10', '20', '50', '100'],
                    showTotal: (total, range) => `显示第 ${range[0]} 到第 ${range[1]} 条记录，共 ${total} 条记录`,
                    onShowSizeChange: this.changePageSize,
                    onChange: this.handleChangePage,
                }}
                scroll={{ x: 1450, y: 360 }}
            />
        );
    }
}

export default ProjectTable;
